import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const INDUSTRIES = [
  { id: 'logistics', label: 'Logistics', icon: 'Truck', desc: 'Delivery & supply chain' },
  { id: 'bfsi', label: 'BFSI', icon: 'Landmark', desc: 'Banking & finance' },
  { id: 'education', label: 'Education', icon: 'GraduationCap', desc: 'Learning & counselling' },
  { id: 'medical', label: 'Medical', icon: 'Stethoscope', desc: 'Healthcare & clinics' },
  { id: 'professional', label: 'Professional Services', icon: 'Briefcase', desc: 'B2B & consulting' },
  { id: 'ecommerce', label: 'Ecommerce', icon: 'ShoppingBag', desc: 'Retail & online stores' },
  { id: 'recruitment', label: 'Recruitment', icon: 'Users', desc: 'Hiring & HR' },
];

const TEMPLATES = [
  { id: 1, name: 'EMI Reminder', icon: 'CreditCard', industry: 'bfsi', tag: 'BFSI' },
  { id: 2, name: 'Delivery Confirmation', icon: 'PackageCheck', industry: 'logistics', tag: 'Logistics', callPurpose: 'Successfully confirm whether the customer will be available to receive the package and collect any important delivery instructions.' },
  { id: 3, name: 'Appointment Reminder', icon: 'CalendarClock', industry: 'medical', tag: 'Medical', callPurpose: 'The primary goal is to determine the patient\'s appointment status by: confirming attendance, helping reschedule if needed, recording cancellations, or routing requests that require human assistance.' },
  { id: 4, name: 'Payment Follow-up', icon: 'DollarSign', industry: 'bfsi', tag: 'BFSI', callPurpose: 'The primary goal is to determine the status of the outstanding payment and achieve one of the following outcomes: Payment already completed. Customer agrees to make payment immediately. Customer provides a payment commitment date. Customer requests payment assistance. Customer requests transfer to a human representative. Incorrect contact identified.' },
  { id: 5, name: 'Interview Scheduler', icon: 'CalendarCheck', industry: 'recruitment', tag: 'Recruitment', callPurpose: 'The primary goal is to successfully schedule an interview by confirming the candidate\'s availability and recording the agreed interview details. If scheduling cannot be completed, determine the appropriate next step such as a callback, rescheduling request, or transfer to a recruiter.' },
  { id: 6, name: 'Customer Support Agent', icon: 'Headphones', industry: 'professional', tag: 'Professional', callPurpose: 'Resolve customer queries accurately while creating a positive customer experience. When resolution isn\'t possible, collect all required information and route the customer to the appropriate support team.' },
  { id: 7, name: 'Lead Qualification Agent', icon: 'Target', industry: 'professional', tag: 'Professional', callPurpose: 'Identify whether the lead is qualified by understanding: Property type, Budget, Preferred location, Purchase timeline, Financing status, Purpose of purchase, Decision-making authority. Once qualified, route the lead to the appropriate sales consultant.' },
  { id: 8, name: 'Order Confirmation', icon: 'ShoppingCart', industry: 'ecommerce', tag: 'Ecommerce', callPurpose: 'Successfully verify the order and obtain the customer\'s confirmation so that the warehouse can proceed with dispatch. If changes are required, capture them accurately and update or escalate before shipment.' },
  { id: 9, name: 'Cart Recovery Agent', icon: 'ShoppingBag', industry: 'ecommerce', tag: 'Ecommerce', callPurpose: 'Understand why the customer abandoned their cart, answer their questions, resolve concerns, and encourage them to complete the purchase whenever appropriate. If the customer isn\'t ready, capture useful feedback for future follow-up.' },
  { id: 10, name: 'Return/Refund Agent', icon: 'RotateCcw', industry: 'ecommerce', tag: 'Ecommerce', callPurpose: 'Resolve the customer\'s request by: Understanding the issue, Collecting all necessary details, Determining eligibility, Initiating the correct return, exchange, or refund process, and Clearly explaining the next steps.' },
  { id: 11, name: 'Insurance Renewal Reminder', icon: 'Shield', industry: 'bfsi', tag: 'BFSI', callPurpose: 'Encourage the customer to renew their policy by: Creating awareness of the renewal timeline, Explaining the importance of continuous coverage, Resolving concerns, and Assisting with the next steps for renewal. The AI should facilitate the renewal process but should not provide regulated financial advice or recommend specific insurance products beyond approved information.' },
  { id: 12, name: 'Education Counselling Agent', icon: 'BookOpen', industry: 'education', tag: 'Education', callPurpose: 'Help prospective students make informed decisions by: Understanding their educational goals, Recommending relevant programs, Explaining the admission process, Answering admission-related questions, Capturing lead information, and Scheduling counselling or application follow-up when appropriate.' },
  { id: 13, name: 'Loan Collection Agent', icon: 'Banknote', industry: 'bfsi', tag: 'BFSI', callPurpose: 'Encourage the customer to make or commit to a loan payment while maintaining a respectful and empathetic conversation. Capture repayment intent, expected payment date, and any hardship information that requires follow-up.' },
  { id: 14, name: 'Product Recommendation Agent', icon: 'Sparkles', industry: 'ecommerce', tag: 'Ecommerce', callPurpose: 'Help customers choose the most suitable skincare products while creating a personalized skincare routine whenever appropriate. Guide customers toward purchasing products that fit their skin concerns without overselling.' },

  { id: 16, name: 'Shipping Update Agent', icon: 'Truck', industry: 'logistics', tag: 'Logistics', callPurpose: 'Provide customers with accurate shipment updates, answer delivery-related questions, resolve issues where possible, and ensure customers clearly understand the next steps.' },
  { id: 17, name: 'Demo Booking Agent', icon: 'Video', industry: 'professional', tag: 'Professional', callPurpose: 'Understand the caller\'s business needs, determine whether they are a qualified prospect, answer introductory questions, collect the required qualification information, and successfully book a product demonstration.' },
  { id: 18, name: 'Event Reminder Agent', icon: 'Bell', industry: 'education', tag: 'Education', callPurpose: 'Ensure the recipient is aware of the event, understands its value, confirms attendance if possible, and receives any additional information they need before attending.' },
  { id: 19, name: 'Feedback Collection Agent', icon: 'MessageSquare', industry: 'professional', tag: 'Professional', callPurpose: 'Collect a valid NPS score, understand the reason behind the rating, gather actionable feedback, and identify whether any follow-up from the hotel is required.' },

];

const TAG_COLORS = {
  BFSI: 'bg-blue-50 text-blue-600',
  Logistics: 'bg-orange-50 text-orange-600',
  Medical: 'bg-red-50 text-red-600',
  Education: 'bg-purple-50 text-purple-600',
  Professional: 'bg-teal-50 text-teal-600',
  Ecommerce: 'bg-pink-50 text-pink-600',
  Recruitment: 'bg-yellow-50 text-yellow-700',
};

const CreateAIAssistantModal = ({ isOpen, onClose, onOpenCreationFlow }) => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);

  if (!isOpen) return null;

  const filteredTemplates = selectedIndustry
    ? TEMPLATES?.filter((t) => t?.industry === selectedIndustry)
    : TEMPLATES;

  const handleIndustryClick = (id) => {
    setSelectedIndustry((prev) => (prev === id ? null : id));
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full h-full sm:w-[70vw] sm:h-[80vh] sm:max-w-[1200px]"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Icon name="Bot" size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Create AI Assistant</h2>
              <p className="text-xs text-gray-500">Choose a template or start from scratch</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT SECTION — 30% (hidden on mobile) */}
          <div className="hidden md:flex w-[30%] border-r border-gray-100 flex-col overflow-y-auto bg-gray-50/50 p-4 gap-3">
            {/* Create Blank Template */}
            <button
              onClick={() => onOpenCreationFlow()}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-br from-violet-500 to-indigo-600 text-white rounded-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon name="FilePlus" size={20} className="text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-sm">Create Blank Template</p>
                <p className="text-xs text-white/75 mt-0.5">Start with a clean slate</p>
              </div>
              <Icon name="ArrowRight" size={16} className="text-white/70 ml-auto group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Industry Templates */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Select Industry Templates</p>
              <div className="flex flex-col gap-1.5">
                {INDUSTRIES?.map((ind) => {
                  const isActive = selectedIndustry === ind?.id;
                  return (
                    <button
                      key={ind?.id}
                      onClick={() => handleIndustryClick(ind?.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 text-left ${
                        isActive
                          ? 'border-violet-400 bg-violet-50 shadow-sm'
                          : 'border-transparent bg-white hover:border-gray-200 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-violet-100' : 'bg-gray-100'}`}>
                        <Icon name={ind?.icon} size={15} className={isActive ? 'text-violet-600' : 'text-gray-500'} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isActive ? 'text-violet-700' : 'text-gray-700'}`}>{ind?.label}</p>
                        <p className="text-xs text-gray-400 truncate">{ind?.desc}</p>
                      </div>
                      {isActive && (
                        <div className="ml-auto flex-shrink-0 w-2 h-2 rounded-full bg-violet-500" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT SECTION — 70% */}
          <div className="flex-1 flex flex-col overflow-hidden p-4 sm:p-5">
            {/* Create From Scratch */}
            <button
              onClick={() => onOpenCreationFlow()}
              className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-indigo-50 to-violet-50 border-2 border-dashed border-indigo-200 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-200 group mb-4 flex-shrink-0"
            >
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Icon name="Wand2" size={20} className="text-indigo-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-indigo-700 text-sm">Create From Scratch</p>
                <p className="text-xs text-indigo-500">Build a fully custom AI assistant tailored to your needs</p>
              </div>
              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-medium text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full hidden sm:inline">Recommended</span>
                <Icon name="ArrowRight" size={16} className="text-indigo-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            {/* Templates Header */}
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <div>
                <p className="text-sm font-semibold text-gray-800">Predefined AI Assistant Templates</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {selectedIndustry
                    ? `${filteredTemplates?.length} templates for ${INDUSTRIES?.find((i) => i?.id === selectedIndustry)?.label}`
                    : `${TEMPLATES?.length} ready-to-use templates`}
                </p>
              </div>
              {selectedIndustry && (
                <button
                  onClick={() => setSelectedIndustry(null)}
                  className="text-xs text-violet-600 hover:text-violet-800 flex items-center gap-1 transition-colors"
                >
                  <Icon name="X" size={12} />
                  Clear filter
                </button>
              )}
            </div>

            {/* Templates Grid */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 pb-2">
                {filteredTemplates?.map((tpl) => (
                  <button
                    key={tpl?.id}
                    onClick={() => onOpenCreationFlow(tpl)}
                    className="flex items-start gap-3 p-3.5 bg-white border border-gray-100 rounded-xl hover:border-violet-200 hover:shadow-md hover:bg-violet-50/30 transition-all duration-150 text-left group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-gray-50 group-hover:bg-violet-100 flex items-center justify-center flex-shrink-0 transition-colors">
                      <Icon name={tpl?.icon} size={16} className="text-gray-500 group-hover:text-violet-600 transition-colors" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 leading-tight mb-1">{tpl?.name}</p>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-md ${TAG_COLORS?.[tpl?.tag] || 'bg-gray-100 text-gray-500'}`}>
                        {tpl?.tag}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAIAssistantModal;
